import { allocId, Player, Shrine, ShrineType, World, xpForLevel, xpGemTier } from './world'
import { Enemy, Pickup, Projectile } from './types'
import { clamp, dirTo, dist, dist2, TAU } from '../core/math'
import { terrainGradient, terrainHeight } from '../core/terrain'
import { WEAPONS } from '../data/weapons'
import { BOSS_LIST, eligibleEnemies, ENEMIES } from '../data/enemies'
import { dealDamage, healPlayer, killEnemy, makeEnemyFromDef, makeProjectile, rollDamage } from './combat'
import { nearestEnemy, toughestEnemy, enemyTowardFacing } from './aim'
import { applyArmor } from './stats'
import { buildSpatialGrid, gridNeighbors } from './grid'
import { bossPattern } from './bossfx'
import { gemRadius } from './footprint'

export interface Intent {
  mx: number
  mz: number
  jump?: boolean
  slide?: boolean
  /** Marksman manual-aim: world-space angle the player is aiming at (mouse/right-stick). When set and the active
   *  hero is the Marksman, it overrides movement-facing so you strafe-and-aim. Absent in headless sims (deterministic). */
  aim?: number
}

const DASH_TIME = 0.16
export const DASH_CD = 2.4 // exported so the HUD can draw a recharge readout (1 - dashCd/DASH_CD)
const INPUT_BUFFER = 0.12 // seconds a jump/slide press is remembered before it's legal (input buffering — snappy controls)
const DASH_SPEED = 3.4
/** Gradient (rise/run) above which a slope is an unclimbable cliff — you slide along it, not up it.
 *  Tuned to sit above gentle maps' max slope (~0.4) and below the harsh map's (~0.77). */
export const CLIFF_SLOPE = 0.6
/** Downhill momentum strength (units/s² per unit of gradient) — gentle so swarm-dodging stays fluid. */
const SLOPE_ACCEL = 4
/** Drop (world units) beyond which stepping off counts as a ledge → you go airborne instead of snapping down. */
const STEP_DOWN = 0.4
/** Gravity (units/s²) for the jump/fall arc. */
const GRAVITY = 22
// MP1: each enemy can only land a contact hit this often, but DISTINCT enemies hit independently —
// so a tight pile deals far more DPS than a single body, giving swarm density real physical weight.
const CONTACT_CD = 0.6

/** Push an entity out of any solid obstacle it overlaps (circle-vs-circle). */
function pushOutOfObstacles(world: World, ent: { x: number; z: number; radius: number }): void {
  for (const o of world.obstacles) {
    const dx = ent.x - o.x
    const dz = ent.z - o.z
    const minD = ent.radius + o.radius
    const d2 = dx * dx + dz * dz
    if (d2 < minD * minD) {
      const d = Math.sqrt(d2) || 0.001
      ent.x = o.x + (dx / d) * minD
      ent.z = o.z + (dz / d) * minD
    }
  }
}

export function updatePlayer(world: World, dt: number, intent: Intent): void {
  const p = world.player
  const s = world.stats
  if (!p.alive) return

  // input buffering: a jump/slide pressed slightly before it's legal (mid-cooldown / mid-air) is remembered for
  // INPUT_BUFFER seconds and fires the instant it becomes legal, instead of being silently dropped — snappy controls.
  p.slideBuf = intent.slide ? INPUT_BUFFER : Math.max(0, p.slideBuf - dt)
  p.jumpBuf = intent.jump ? INPUT_BUFFER : Math.max(0, p.jumpBuf - dt)

  // slide (Shift) — fast low burst with i-frames; on terrain it gains a directional identity
  // (a downhill slide runs longer, an uphill one is shorter)
  if (p.slideBuf > 0 && p.dashCd <= 0 && (intent.mx !== 0 || intent.mz !== 0)) {
    p.slideBuf = 0
    let dashMul = 1
    if (world.terrainAmp > 0) {
      const g = terrainGradient(p.x, p.z, world.terrainSeed, world.terrainAmp, world.terrainCfg)
      const il = Math.hypot(intent.mx, intent.mz) || 1
      const downhill = -((intent.mx / il) * g.gx + (intent.mz / il) * g.gz) // >0 = sliding downhill
      dashMul = clamp(1 + downhill * 0.7, 0.6, 1.8)
    }
    p.dashTime = DASH_TIME * dashMul
    p.dashCd = DASH_CD
    p.invuln = Math.max(p.invuln, p.dashTime + 0.12)
    world.burst(p.x, p.z, 0x9fe8ff, 8, 0.7)
  }
  // jump (Space) — brief airborne arc; dodges ground stuff and clears gaps/ledges while up
  if (p.jumpBuf > 0 && p.jumpCd <= 0 && p.y <= 0.05) {
    p.jumpBuf = 0
    p.vy = 9
    p.jumpCd = 1.1
    world.burst(p.x, p.z, 0xffe27a, 6, 0.6)
  }

  const dashing = p.dashTime > 0
  const speed = s.moveSpeed * (dashing ? DASH_SPEED : 1)
  const moving = intent.mx !== 0 || intent.mz !== 0
  const tvx = intent.mx * speed
  const tvz = intent.mz * speed
  if (dashing) {
    // dash stays crisp — snap to the dash velocity
    p.vx = tvx
    p.vz = tvz
  } else {
    // give the body mass: ease velocity toward the target (accel) / toward 0 (decel)
    // so the hero carries momentum instead of teleport-snapping like a cursor
    const blend = Math.min(1, (moving ? 13 : 10) * dt)
    p.vx += (tvx - p.vx) * blend
    p.vz += (tvz - p.vz) * blend
    if (!moving && Math.abs(p.vx) < 0.02 && Math.abs(p.vz) < 0.02) {
      p.vx = 0
      p.vz = 0
    }
  }

  // terrain shapes horizontal motion: cliffs block the climb, hills add downhill momentum.
  // Fully gated on amp>0 so flat maps stay byte-identical (no new float ops execute).
  if (world.terrainAmp > 0) {
    const g = terrainGradient(p.x, p.z, world.terrainSeed, world.terrainAmp, world.terrainCfg)
    const gmag = Math.hypot(g.gx, g.gz)
    if (gmag > 1e-4) {
      // can't climb a cliff: strip the uphill component of velocity (you slide along its base)
      if (gmag > CLIFF_SLOPE) {
        const hx = g.gx / gmag
        const hz = g.gz / gmag
        const vAlong = p.vx * hx + p.vz * hz // >0 = pushing uphill
        if (vAlong > 0) {
          p.vx -= vAlong * hx
          p.vz -= vAlong * hz
        }
      }
      // momentum down the slope (−gradient) — kept gentle so the swarm-dodge core stays fluid
      p.vx -= g.gx * SLOPE_ACCEL * dt
      p.vz -= g.gz * SLOPE_ACCEL * dt
    }
  }

  // ground height at the pre-move position (for absolute-height ledge/fall detection)
  const groundPrev = world.terrainAmp > 0 ? terrainHeight(p.x, p.z, world.terrainSeed, world.terrainAmp, world.terrainCfg) : 0
  p.x += p.vx * dt
  p.z += p.vz * dt

  // face / track the direction we're actually travelling (keeps facing during glide-out)
  if (Math.abs(p.vx) > 0.05 || Math.abs(p.vz) > 0.05) {
    p.facing = Math.atan2(p.vz, p.vx)
    const vlen = Math.hypot(p.vx, p.vz) || 1
    p.moveDir.x = p.vx / vlen
    p.moveDir.z = p.vz / vlen
  }
  // Marksman manual-aim: a supplied aim angle overrides movement-facing, so you strafe one way while shooting another.
  // Only when the active hero has the passive AND the layer fed an aim (mouse/right-stick); headless sims never set it.
  if (world.marksman && intent.aim != null) p.facing = intent.aim

  // arena bounds (circular)
  const r = world.map.radius - 1.5
  const d = Math.hypot(p.x, p.z)
  if (d > r) {
    p.x = (p.x / d) * r
    p.z = (p.z / d) * r
  }
  if (world.obstacles.length > 0) pushOutOfObstacles(world, p)

  // vertical: jump arc + gravity. On terrain (amp>0) integrate the player's ABSOLUTE feet
  // height so stepping off a ledge makes you fall/arc and a jump lands on whatever ground is
  // under you (gaps & ledges become real). Flat maps keep the original byte-identical path.
  if (world.terrainAmp > 0) {
    const groundNew = terrainHeight(p.x, p.z, world.terrainSeed, world.terrainAmp, world.terrainCfg)
    let feet = groundPrev + p.y
    if (p.y <= 0.05 && p.vy <= 0) {
      // was grounded: snap-follow gentle slopes, but go airborne off a real ledge drop
      if (groundPrev - groundNew > STEP_DOWN) {
        feet = groundPrev // keep height and fall
      } else {
        feet = groundNew // glue to the ground (walk up/down gentle slopes)
        p.vy = 0
      }
    }
    p.vy -= GRAVITY * dt
    feet += p.vy * dt
    if (feet <= groundNew) {
      feet = groundNew
      p.vy = 0
    }
    p.y = feet - groundNew
  } else if (p.y > 0 || p.vy > 0) {
    p.y += p.vy * dt
    p.vy -= 22 * dt
    if (p.y <= 0) {
      p.y = 0
      p.vy = 0
    }
  }
  if (p.y > 0.25) p.invuln = Math.max(p.invuln, 0.08) // i-frames while airborne

  // timers
  p.invuln = Math.max(0, p.invuln - dt)
  p.dashCd = Math.max(0, p.dashCd - dt)
  p.dashTime = Math.max(0, p.dashTime - dt)
  p.jumpCd = Math.max(0, p.jumpCd - dt)
  p.flash = Math.max(0, p.flash - dt)

  // regenerating shield (maxShield stat): trickle back up to capacity while not actively being struck (p.flash is set on
  // every hit, so a steady stream of damage keeps the shield suppressed). Never reduces an over-shield granted elsewhere.
  const ms = s.maxShield
  if (ms > 0 && p.shield < ms && p.flash <= 0) {
    p.shield = Math.min(ms, p.shield + (1.5 + ms * 0.1) * dt)
  }

  // regen
  if (s.hpRegen > 0 && p.hp < s.maxHp) healPlayer(world, s.hpRegen * dt)
}

export function fireWeapons(world: World, dt: number): void {
  const s = world.stats
  if (world.weaponsLocked > 0) world.weaponsLocked = Math.max(0, world.weaponsLocked - dt)
  for (let wi = 0; wi < world.weapons.length; wi++) {
    const ws = world.weapons[wi]
    // boss weapon-lock: only the starting weapon (slot 0) may fire
    if (world.weaponsLocked > 0 && wi > 0) continue
    const def = WEAPONS[ws.defId]
    if (!def) continue
    ws.cooldown -= dt
    if (ws.cooldown <= 0) {
      const rawCd = def.baseCooldown(ws.level) / s.attackSpeed
      const cd = Math.max(0.03, rawCd) // 0.03s fire-rate floor (perf + sanity cap)
      // resolve this shot's target according to the weapon's aim policy
      let target: Enemy | null = null
      // Marksman hero: auto-targeting weapons instead lock onto the foe you're AIMING at (cone toward facing) — you
      // shoot where you point. Heavy 'toughest' seekers still favour the biggest threat in that direction.
      if (world.marksman && (def.aim === 'nearest' || def.aim === 'toughest')) {
        target = enemyTowardFacing(world, world.player.x, world.player.z, world.player.facing, def.aimRange ?? Infinity)
      } else if (def.aim === 'nearest') target = nearestEnemy(world, world.player.x, world.player.z, def.aimRange ?? Infinity)
      else if (def.aim === 'toughest') target = toughestEnemy(world, world.player.x, world.player.z, def.aimRange ?? Infinity)
      // weapons that need a lock-on don't whiff into empty space: keep the shot ready, recheck soon
      if (def.requiresTarget && !target) {
        ws.cooldown = Math.min(0.12, cd)
        continue
      }
      ws.cooldown += cd
      if (ws.cooldown < 0) ws.cooldown = cd
      // evolved weapons grow with their level (+16% damage/level); plus: when attackSpeed pushes the cadence below
      // the 0.03s floor, the "lost" rate is repaid as per-shot DAMAGE so attackSpeed never becomes a dead stat past
      // the breakpoint (DPS keeps scaling without spawning more projectiles). Both applied in rollDamage.
      const overflowMul = rawCd < cd ? cd / rawCd : 1
      world.evoDamageMul = (def.hidden && ws.level > 1 ? 1 + 0.16 * (ws.level - 1) : 1) * overflowMul
      def.fire({ world, ws, level: ws.level, stats: s, target })
      world.evoDamageMul = 1
      world.shotsThisFrame++
      // muzzle flash + heavy-weapon recoil kick (heavy = long base cooldown)
      world.burst(world.player.x, world.player.z, 0xffe27a, 2, 0.5)
      if (def.baseCooldown(ws.level) >= 1.4) world.recoilThisFrame = true
    }
  }
}

/** Character signature gimmicks that fire periodically (shockwave is on-kill, in combat). */
export function updateGimmick(world: World, dt: number): void {
  const g = world.gimmick
  if (!g || g === 'shockwave' || g === 'dice') return // shockwave is on-kill (combat.ts); dice rolls at level-up (checkLevelUp)
  const p = world.player
  if (!p.alive) return
  world.gimmickCd -= dt
  if (world.gimmickCd > 0) return
  const s = world.stats
  // MP7: gimmick damage runs through the SAME rollDamage pipeline as weapons — so the player's
  // DAMAGE stat, crit chance/mult, Critical Mass and damage passives all power the character's
  // signature, instead of the old flat (time*coeff) that ignored build investment. The coefficient
  // is the per-gimmick "weapon base"; rollDamage multiplies in s.damage + rolls crit + variance.
  const coeff = 4 + world.time * 0.12
  switch (g) {
    case 'aura': {
      world.gimmickCd = 0.4
      const radius = (2.6 + s.area * 0.8) * 1
      const d = rollDamage(world, coeff)
      world.spawnProjectile(
        makeProjectile({ ownerWeapon: 'gim_aura', behavior: 'aura', x: p.x, z: p.z, damage: d.amount, crit: d.crit, life: 0.45, pierce: 999, radius, color: 0x9fe8ff, slow: 0.6, hitCooldown: 0.45 }),
      )
      break
    }
    case 'orbit': {
      world.gimmickCd = 2.2
      const count = 2 + s.projectileCount
      const life = 2.4 * s.duration
      for (let i = 0; i < count; i++) {
        const a = (i / count) * TAU
        const d = rollDamage(world, coeff * 1.4)
        world.spawnProjectile(
          makeProjectile({ ownerWeapon: 'gim_orbit', behavior: 'orbit', x: p.x, z: p.z, damage: d.amount, crit: d.crit, life, pierce: 999, radius: 0.5 * s.area, color: 0x3fd6c0, angle: a, orbitRadius: 2.8 * s.area, orbitSpeed: 3.4 * s.projectileSpeed, knockback: 0.6, hitCooldown: 0.4 }),
        )
      }
      break
    }
    case 'volley': {
      world.gimmickCd = 3
      const count = 10 + s.projectileCount
      for (let i = 0; i < count; i++) {
        const a = (i / count) * TAU
        const speed = 20 * s.projectileSpeed
        const d = rollDamage(world, coeff * 0.9)
        world.spawnProjectile(
          makeProjectile({ ownerWeapon: 'gim_volley', behavior: 'straight', x: p.x, z: p.z, vx: Math.cos(a) * speed, vz: Math.sin(a) * speed, speed, damage: d.amount, crit: d.crit, life: 0.7 * s.duration, pierce: 1, radius: 0.3 * s.area, color: 0xffe27a, knockback: 0.4 }),
        )
      }
      break
    }
    case 'nova': {
      // witch (Hazel): a hard-hitting arcane NOVA erupts AROUND her (aura is player-centered) — burst-damage identity,
      // distinct from her targeted Meteor starting weapon
      world.gimmickCd = 2.6
      const d = rollDamage(world, coeff * 2.2)
      world.spawnProjectile(
        makeProjectile({ ownerWeapon: 'gim_nova', behavior: 'aura', x: p.x, z: p.z, damage: d.amount, crit: d.crit, life: 0.35, pierce: 999, radius: 3.0 * s.area, color: 0xb06cff, knockback: 1.0, hitCooldown: 0.35 }),
      )
      world.burst(p.x, p.z, 0xb06cff, 26, 1.8)
      break
    }
    case 'mortar': {
      // engineer (Dot): lobs an explosive shell onto a nearby enemy — a real targeted impact (lobbed → explodes where it
      // lands, x/z fixed). Artillery "dakka" identity, distinct from the player-centered nova.
      world.gimmickCd = 2.4
      const tgt = nearestEnemy(world, p.x, p.z, 24)
      const tx = tgt ? tgt.x : p.x + Math.cos(p.facing) * 7
      const tz = tgt ? tgt.z : p.z + Math.sin(p.facing) * 7
      const d = rollDamage(world, coeff * 1.6)
      world.spawnProjectile(
        makeProjectile({ ownerWeapon: 'gim_mortar', behavior: 'lobbed', x: tx, z: tz, y: 12, vy: 0, damage: 0, crit: d.crit, life: 1.2, radius: 0.6, color: 0x4aa0c2, explodeRadius: 2.4 * s.area, explodeDamage: d.amount, knockback: 1.2 }),
      )
      break
    }
    case 'ricochet': {
      // pip: a quick burst of light bouncing bolts aimed at the nearest foe — "fires like mad"
      world.gimmickCd = 1.4
      const count = 2 + s.projectileCount
      const tgt = nearestEnemy(world, p.x, p.z, 30)
      const baseA = tgt ? Math.atan2(tgt.z - p.z, tgt.x - p.x) : p.facing
      const speed = 22 * s.projectileSpeed
      for (let i = 0; i < count; i++) {
        const a = baseA + (i - (count - 1) / 2) * 0.18
        const d = rollDamage(world, coeff * 0.8)
        world.spawnProjectile(
          makeProjectile({ ownerWeapon: 'gim_ricochet', behavior: 'straight', x: p.x, z: p.z, vx: Math.cos(a) * speed, vz: Math.sin(a) * speed, speed, damage: d.amount, crit: d.crit, life: 0.9 * s.duration, pierce: 1, bounce: 2, radius: 0.28 * s.area, color: 0x66e0ff, knockback: 0.3 }),
        )
      }
      break
    }
    case 'repair': {
      // sustain (Pulsar): a periodic self-repair pulse — heals a chunk + tops up a small shield (attrition identity)
      world.gimmickCd = 3.5
      healPlayer(world, world.stats.maxHp * 0.05)
      p.shield = Math.max(p.shield, 25)
      world.burst(p.x, p.z, 0x6fd06a, 14, 1.0)
      break
    }
    case 'forage': {
      // forager (Stash): periodically unearths a free cache of gold near you — an economy engine, not a stat passive.
      // Scales gently with run time + gold-gain so it stays relevant. Deterministic position via world.rng.
      world.gimmickCd = 6
      const a = world.rng.next() * TAU
      const r = 1.5 + world.rng.next() * 2
      const amt = Math.max(4, Math.round((5 + world.time * 0.08) * s.goldGain))
      world.spawnPickup('gold', p.x + Math.cos(a) * r, p.z + Math.sin(a) * r, amt)
      world.burst(p.x, p.z, 0xffd23b, 10, 0.8)
      break
    }
  }
}

// ---------------------------------------------------------------------------
// Projectiles
// ---------------------------------------------------------------------------

export function updateProjectiles(world: World, dt: number): void {
  const p = world.player
  for (const pr of world.projectiles) {
    if (!pr.alive) continue

    switch (pr.behavior) {
      case 'straight': {
        // instant chain bolts have speed 0 and chain > 0
        if (pr.speed === 0 && pr.chain > 0) {
          resolveChain(world, pr)
          pr.alive = false
          continue
        }
        pr.x += pr.vx * dt
        pr.z += pr.vz * dt
        break
      }
      case 'homing': {
        const t = nearestEnemy(world, pr.x, pr.z, 30)
        if (t) {
          const to = dirTo(pr.x, pr.z, t.x, t.z)
          const curA = Math.atan2(pr.vz, pr.vx)
          const tgtA = Math.atan2(to.z, to.x)
          let da = tgtA - curA
          while (da > Math.PI) da -= TAU
          while (da < -Math.PI) da += TAU
          const turn = clamp(da, -pr.homingTurn * dt, pr.homingTurn * dt)
          const na = curA + turn
          pr.vx = Math.cos(na) * pr.speed
          pr.vz = Math.sin(na) * pr.speed
        }
        pr.x += pr.vx * dt
        pr.z += pr.vz * dt
        break
      }
      case 'orbit':
      case 'spinner': {
        pr.angle += pr.orbitSpeed * dt
        pr.x = p.x + Math.cos(pr.angle) * pr.orbitRadius
        pr.z = p.z + Math.sin(pr.angle) * pr.orbitRadius
        break
      }
      case 'boomerang': {
        const to = dirTo(pr.x, pr.z, p.x, p.z)
        const accel = 30
        pr.vx += to.x * accel * dt
        pr.vz += to.z * accel * dt
        pr.x += pr.vx * dt
        pr.z += pr.vz * dt
        // detect return phase: moving toward player
        const dot = pr.vx * to.x + pr.vz * to.z
        if (dot > 0) pr.returnedToPlayer = true
        if (pr.returnedToPlayer && to.len < 1.0) {
          pr.alive = false
          continue
        }
        break
      }
      case 'aura': {
        pr.x = p.x
        pr.z = p.z
        break
      }
      case 'lobbed': {
        pr.y -= (14 / 0.9) * dt
        if (pr.y <= 0.4) {
          explode(world, pr)
          pr.alive = false
          continue
        }
        break
      }
      case 'beam': {
        pr.x += pr.vx * dt
        pr.z += pr.vz * dt
        break
      }
    }

    // gravitational pull: gather enemies from an outer shell toward the aura,
    // stopping at the inner radius so they cluster in the kill zone (not on the player)
    if (pr.pull > 0) {
      const inner = pr.radius
      const outer = pr.radius * 2.4
      for (const e of world.enemies) {
        if (!e.alive || e.isBoss) continue
        const dx = pr.x - e.x
        const dz = pr.z - e.z
        const d = Math.hypot(dx, dz)
        if (d > inner && d < outer) {
          const f = (pr.pull * dt) / d
          e.x += dx * f
          e.z += dz * f
        }
      }
    }

    // periodic hitSet clear for multi-hit projectiles
    if (pr.hitCooldown > 0) {
      pr.hitClear -= dt
      if (pr.hitClear <= 0) {
        pr.hitSet.clear()
        pr.hitClear = pr.hitCooldown
        pr.chain = pr.chainBase // re-arm chain lightning for the new hit cycle (e.g. Storm Glaive)
      }
    }

    pr.life -= dt
    if (pr.life <= 0) {
      if (pr.onExpireSpawn) {
        // reserved for future on-expire effects
      }
      pr.alive = false
    }

    // out-of-arena cull for moving projectiles
    if (pr.behavior === 'straight' || pr.behavior === 'homing') {
      if (dist2(pr.x, pr.z, 0, 0) > (world.map.radius + 6) ** 2) pr.alive = false
    }
  }
}

function explode(world: World, pr: Projectile): void {
  world.burst(pr.x, pr.z, pr.color, 26, 1.6)
  const r2 = pr.explodeRadius * pr.explodeRadius
  for (const e of world.enemies) {
    if (!e.alive) continue
    if (dist2(pr.x, pr.z, e.x, e.z) <= r2) {
      const to = dirTo(pr.x, pr.z, e.x, e.z)
      dealDamage(world, e, pr.explodeDamage, {
        crit: pr.crit,
        knockX: to.x,
        knockZ: to.z,
        knockForce: pr.knockback * 4,
        source: pr.ownerWeapon,
        bypassArmor: true, // AoE blasts ignore front-armor
      })
      if (pr.burn > 0) { applyBurn(e, pr.burn, pr.explodeDamage * 0.12); e.burnSrc = pr.ownerWeapon }
      if (pr.poison > 0) { applyPoison(e, pr.poison, pr.explodeDamage * 0.1); e.poisonSrc = pr.ownerWeapon }
      if (pr.slow > 0) {
        e.slow = Math.max(e.slow, 1.2)
        e.slowAmt = Math.max(e.slowAmt, pr.slow)
      }
      if (pr.freeze > 0) e.freeze = Math.max(e.freeze, pr.freeze)
    }
  }
}

/** Instant chain lightning starting at the projectile's position. */
function resolveChain(world: World, pr: Projectile): void {
  let cx = pr.x
  let cz = pr.z
  let dmg = pr.damage
  const hit = new Set<number>()
  const jumps = Math.max(1, pr.chain)
  for (let j = 0; j < jumps; j++) {
    // find nearest unhit enemy
    let best: Enemy | null = null
    let bestD = j === 0 ? 4 * 4 : 7 * 7
    for (const e of world.enemies) {
      if (!e.alive || hit.has(e.id)) continue
      const d = dist2(cx, cz, e.x, e.z)
      if (d < bestD) {
        bestD = d
        best = e
      }
    }
    if (!best) break
    hit.add(best.id)
    // lightning particle trail
    chainBolt(world, cx, cz, best.x, best.z, pr.color)
    const to = dirTo(cx, cz, best.x, best.z)
    dealDamage(world, best, dmg, {
      crit: pr.crit,
      knockX: to.x,
      knockZ: to.z,
      knockForce: pr.knockback * 2,
      source: pr.ownerWeapon,
      // chain damage IS a real weapon hit — it must drive the on-hit/on-crit proc layer, Conduction and
      // frost-shatter (Zapper/Tempest deal ALL their damage through here). Exclude proc-spawned bolts so a
      // Static Discharge arc can't recursively re-proc itself each tick.
      weaponHit: pr.ownerWeapon !== 'static' && pr.ownerWeapon !== 'harvest',
    })
    cx = best.x
    cz = best.z
    dmg *= 0.85 // slight falloff per jump
  }
}

function chainBolt(world: World, x1: number, z1: number, x2: number, z2: number, color: number): void {
  const steps = 5
  for (let i = 0; i <= steps; i++) {
    const t = i / steps
    world.particles.push({
      id: 0,
      x: x1 + (x2 - x1) * t + (world.rng.next() - 0.5) * 0.4,
      z: z1 + (z2 - z1) * t + (world.rng.next() - 0.5) * 0.4,
      y: 0.8,
      vx: 0,
      vz: 0,
      vy: 0,
      life: 0.12,
      maxLife: 0.12,
      color,
      size: 0.16,
    })
  }
}

// ---------------------------------------------------------------------------
// Collisions: projectiles vs enemies, enemies vs player, pickups vs player
// ---------------------------------------------------------------------------

export function collideProjectiles(world: World): void {
  for (const pr of world.projectiles) {
    if (!pr.alive) continue
    if (pr.behavior === 'lobbed') continue // damage on explosion only
    if (pr.damage < 0 || pr.ownerWeapon === 'enemy') continue // enemy shots are resolved vs the player in collideEnemyProjectiles (else they'd be eaten by their own shooter at spawn)
    for (const e of world.enemies) {
      if (!e.alive) continue
      if (pr.hitSet.has(e.id)) continue
      // collision uses the EFFECTIVE radius (radius*scale) so shots connect on the visible body
      // of scaled enemies (bosses/elites/champions), not a base-size hitbox under a giant sprite
      const rr = pr.radius + e.radius * e.scale
      if (dist2(pr.x, pr.z, e.x, e.z) > rr * rr) continue

      pr.hitSet.add(e.id)
      const to = dirTo(world.player.x, world.player.z, e.x, e.z)
      // orbit/spinner/aura have no travel velocity → push the enemy radially outward (player→enemy)
      const radial = pr.behavior === 'orbit' || pr.behavior === 'spinner' || pr.behavior === 'aura'
      const kx = radial ? to.x : pr.vx
      const kz = radial ? to.z : pr.vz
      const klen = Math.hypot(kx, kz) || 1
      dealDamage(world, e, pr.damage, {
        crit: pr.crit,
        knockX: kx / klen,
        knockZ: kz / klen,
        knockForce: pr.knockback * 3,
        source: pr.ownerWeapon,
        // proc-spawned projectiles don't re-trigger the proc layer (no cascades)
        weaponHit: pr.ownerWeapon !== 'static' && pr.ownerWeapon !== 'harvest',
      })

      // status application (DoT is attributed to the weapon that applied it)
      if (pr.burn > 0) { applyBurn(e, pr.burn, pr.damage * 0.12); e.burnSrc = pr.ownerWeapon }
      if (pr.poison > 0) { applyPoison(e, pr.poison, pr.damage * 0.1); e.poisonSrc = pr.ownerWeapon }
      if (pr.slow > 0) {
        e.slow = Math.max(e.slow, 1.2)
        e.slowAmt = Math.max(e.slowAmt, pr.slow)
      }
      if (pr.freeze > 0) e.freeze = Math.max(e.freeze, pr.freeze)

      // on-hit chain (e.g. stormglaive)
      if (pr.chain > 0 && pr.speed > 0) {
        const bolt = makeProjectile({
          ownerWeapon: pr.ownerWeapon,
          behavior: 'straight',
          x: e.x,
          z: e.z,
          speed: 0,
          damage: pr.damage * 0.6,
          crit: pr.crit,
          chain: pr.chain,
          color: 0xbfe0ff,
          life: 0.05,
        })
        bolt.hitSet.add(e.id)
        world.spawnProjectile(bolt)
        pr.chain = 0 // don't chain again from this projectile this cycle
      }

      // pierce / bounce accounting for single-hit projectiles
      if (pr.hitCooldown === 0) {
        if (pr.pierce > 0) {
          pr.pierce--
        } else if (pr.bounce > 0) {
          // ricochet: redirect toward the nearest not-yet-hit enemy
          let target: Enemy | null = null
          let bestD = 14 * 14
          for (const o of world.enemies) {
            if (!o.alive || pr.hitSet.has(o.id)) continue
            const d = dist2(e.x, e.z, o.x, o.z)
            if (d < bestD) {
              bestD = d
              target = o
            }
          }
          if (target) {
            pr.bounce--
            const to = dirTo(e.x, e.z, target.x, target.z)
            pr.vx = to.x * pr.speed
            pr.vz = to.z * pr.speed
          } else {
            pr.alive = false
            break
          }
        } else {
          pr.alive = false
          break
        }
      }
    }
  }
}

// ---------------------------------------------------------------------------
// Breakables: smash for random loot
// ---------------------------------------------------------------------------

export function collideBreakables(world: World): void {
  if (world.breakables.length === 0) return
  for (const pr of world.projectiles) {
    if (!pr.alive || pr.damage <= 0) continue // skip enemy shots (negative dmg)
    for (const b of world.breakables) {
      if (b.hp <= 0 || pr.hitSet.has(b.id)) continue
      const rr = pr.radius + b.radius
      if (dist2(pr.x, pr.z, b.x, b.z) > rr * rr) continue
      pr.hitSet.add(b.id)
      b.hp -= pr.damage
      b.flash = 0.12
      if (b.hp <= 0) destroyBreakable(world, b)
      if (pr.hitCooldown === 0) {
        if (pr.pierce > 0) pr.pierce--
        else {
          pr.alive = false
          break
        }
      }
    }
  }
  if (world.breakables.some((b) => b.hp <= 0)) world.breakables = world.breakables.filter((b) => b.hp > 0)
}

function destroyBreakable(world: World, b: { x: number; z: number }): void {
  world.burst(b.x, b.z, 0xffd23d, 16, 1.2)
  const r = world.rng.next()
  if (r < 0.6) world.spawnPickup('gold', b.x, b.z, Math.round((3 + world.time * 0.15) * world.rewardMult))
  else if (r < 0.92) world.spawnPickup('xp', b.x, b.z, Math.round(2 + world.time * 0.05))
  else world.spawnPickup('heal', b.x, b.z, 15)
}

// ---------------------------------------------------------------------------
// Enemies: AI movement, separation, status ticking, contact damage
// ---------------------------------------------------------------------------

export function updateEnemies(world: World, dt: number): void {
  const p = world.player
  for (const e of world.enemies) {
    if (!e.alive) continue

    // status: burn / poison / freeze / slow timers
    if (e.burn > 0) {
      e.burn -= dt
      dealDamage(world, e, e.burnDps * dt, { silent: true, lifesteal: false, source: e.burnSrc })
      if (world.rng.next() < 0.05) world.floatText(e.x, e.z, Math.round(e.burnDps * 0.5).toString(), 0xff9a3d)
      if (e.burn <= 0) e.burnDps = 0
      if (!e.alive) continue
    }
    if (e.poison > 0) {
      e.poison -= dt
      // stacking bleed makes re-applied poison ramp up (bleedStacks raises the tick)
      const pdps = e.poisonDps * (1 + e.bleedStacks * 0.04)
      dealDamage(world, e, pdps * dt, { silent: true, lifesteal: false, source: e.poisonSrc })
      if (world.rng.next() < 0.05) world.floatText(e.x, e.z, Math.round(pdps * 0.5).toString(), 0x9acd32)
      if (e.poison <= 0) {
        e.poisonDps = 0
        e.bleedStacks = 0
      }
      if (!e.alive) continue
    }
    let slowFactor = 1
    let frozen = false
    if (e.freeze > 0) {
      e.freeze -= dt
      // Bosses RESIST hard freeze: it heavily SLOWS them instead of fully disabling their moveset/enrage/power.
      // A continuously-refreshed freeze (Glacial Spear/Blizzard) would otherwise perma-lock a boss out of
      // attacking — a free statue that trivializes the climax. (shatter/wasFrozen still read e.freeze.)
      if (e.isBoss) slowFactor = 0.3
      else frozen = true
    }
    if (e.slow > 0) {
      e.slow -= dt
      slowFactor = Math.min(slowFactor, 1 - e.slowAmt) // combine with any boss-freeze slow; take the stronger
      if (e.slow <= 0) e.slowAmt = 0 // clear magnitude on expiry (like burn/poison) so a worn-off strong slow can't bleed into a later weaker one
    }
    e.flash = Math.max(0, e.flash - dt)
    if (e.contactCd > 0) e.contactCd = Math.max(0, e.contactCd - dt)
    if (e.bossShield > 0) e.bossShield = Math.max(0, e.bossShield - dt)
    if (e.affix === 'shielded') {
      e.shieldCd -= dt
      if (e.shieldCd <= 0) { e.bossShield = Math.max(e.bossShield, 0.8); e.shieldCd = 5 } // periodic ~0.8s immunity — wait out the glow
    } else if (e.affix === 'mending' && e.hp < e.maxHp) {
      e.hp = Math.min(e.maxHp, e.hp + e.maxHp * 0.02 * dt) // slow self-heal (2%/s) — out-damage it or it claws back
    }
    if (e.empoweredT > 0) e.empoweredT = Math.max(0, e.empoweredT - dt)

    // knockback velocity decay (stagger) — softened decay so the bonk reads
    if (e.stagger > 0) {
      e.stagger -= dt
      e.x += e.vx * dt
      e.z += e.vz * dt
      e.vx *= Math.pow(0.05, dt)
      e.vz *= Math.pow(0.05, dt)
    }

    // while staggered, the knockback velocity above is the SOLE mover for light enemies — running the chase
    // switch in the same tick would drag them straight back and eat most of the bonk. (separate() already skips
    // staggered bodies; the charger self-drives its dash through the stagger block.) Bosses/elites have tiny
    // knock mass (they barely move), so they must NEVER be stagger-locked out of attacking/chasing.
    if (!frozen && (e.stagger <= 0 || e.isBoss || e.isElite)) {
      // summoners periodically spawn minions
      const sdef = ENEMIES[e.defId]
      if (sdef?.summon) {
        e.summonCd -= dt
        if (e.summonCd <= 0) {
          e.summonCd = sdef.summon.interval
          if (world.enemies.length < 300) {
            for (let i = 0; i < sdef.summon.count; i++) {
              const sa = world.rng.next() * TAU
              world.spawnEnemy(
                makeEnemyFromDef(world, sdef.summon.id, e.x + Math.cos(sa) * 1.3, e.z + Math.sin(sa) * 1.3),
              )
            }
          }
        }
      }

      const to = dirTo(e.x, e.z, p.x, p.z)
      e.facing = Math.atan2(to.z, to.x)
      // empowered foes (buffed by a War-Chanter / Hive Overseer) move ~35% faster
      const sp = e.speed * world.enemySpeedMul * slowFactor * (e.empoweredT > 0 ? 1.35 : 1) * (e.affix === 'swift' ? 1.35 : 1)
      const ox = e.x
      const oz = e.z
      switch (e.ai) {
        case 'chaser':
        case 'armored':
        case 'boss': {
          e.x += to.x * sp * dt
          e.z += to.z * sp * dt
          if (e.ai === 'boss') {
            const bdef = ENEMIES[e.defId]
            // HP-phase: enrage below the threshold → PT3 phase-2 (faster + a NOVA desperation moment)
            if (!e.enraged && bdef?.enrageAt && e.hp <= e.maxHp * bdef.enrageAt) {
              e.enraged = true
              e.speed *= 1.25
              world.floatText(e.x, e.z, 'ENRAGED', 0xff3a2a, true)
              world.burst(e.x, e.z, 0xff3a2a, 40, 2.0)
              world.hitStop = Math.max(world.hitStop, 0.16)
              executeBossPattern(world, e, p, 'nova') // a phase-transition blast clears the area
              e.attackCd = 1.6
            }
            e.attackCd -= dt
            if (e.attackCd <= 0 && to.len < 34) {
              const patterns = bdef?.patterns && bdef.patterns.length > 0 ? bdef.patterns : (['slam'] as const)
              const pat = bossPattern(patterns, e.patternIdx, e.enraged) // PT3: enrage interleaves NOVA
              e.patternIdx++
              const cd = executeBossPattern(world, e, p, pat)
              e.attackCd = e.enraged ? cd * 0.6 : cd
            }
            // signature boss power (independent of the attack-pattern cycle)
            if (bdef?.bossPower === 'gravity') {
              // constant pull dragging the player toward the boss
              const gp = 2.6 * dt
              p.x -= to.x * gp
              p.z -= to.z * gp
            } else if (bdef?.bossPower) {
              e.aiTimer -= dt
              if (e.aiTimer <= 0) {
                e.aiTimer = e.enraged ? 7 : 11
                executeBossPower(world, e, bdef.bossPower)
              }
            }
          }
          break
        }
        case 'ranged': {
          // keep distance ~9, fire at player
          const want = 9
          if (to.len > want + 1) {
            e.x += to.x * sp * dt
            e.z += to.z * sp * dt
          } else if (to.len < want - 1) {
            e.x -= to.x * sp * dt
            e.z -= to.z * sp * dt
          }
          e.attackCd -= dt
          const def = ENEMIES[e.defId]
          if (e.attackCd <= 0 && to.len < 16) {
            e.attackCd = def.rangedCooldown ?? 2.5
            const aim = leadDir(world, e, 9) // lead the player's movement
            spawnEnemyShot(world, e, aim.x, aim.z)
          }
          break
        }
        case 'charger': {
          // wind up then dash toward player periodically
          e.aiTimer -= dt
          if (e.aiTimer <= 0) {
            e.aiTimer = 2.2
            e.vx = to.x * sp * 4
            e.vz = to.z * sp * 4
            e.stagger = Math.max(e.stagger, 0.5)
          } else {
            e.x += to.x * sp * 0.5 * dt
            e.z += to.z * sp * 0.5 * dt
          }
          break
        }
        case 'orbiter': {
          // circle the player while closing in
          const tangent = e.facing + Math.PI / 2
          const closeBias = to.len > 6 ? 0.7 : 0.1
          e.x += (to.x * closeBias + Math.cos(tangent) * 0.7) * sp * dt
          e.z += (to.z * closeBias + Math.sin(tangent) * 0.7) * sp * dt
          break
        }
        case 'splitter': {
          e.x += to.x * sp * dt
          e.z += to.z * sp * dt
          break
        }
        case 'flanker': {
          // swing wide to a flank: strong tangential motion + a light pull/push to keep
          // a strafing arc around the player (distinct from the orbiter's tight circle)
          const tangent = e.facing + (Math.PI / 2) * e.aiSide
          // strafe wide, but keep a SLIGHT inward bias when close so it actually spirals into contact and bites —
          // the old -0.25 push parked it at a ~5u standoff (far outside its ~0.9u hit range) so it dealt zero damage
          const radial = to.len > 3 ? 0.55 : 0.06
          e.x += (to.x * radial + Math.cos(tangent) * 0.95) * sp * dt
          e.z += (to.z * radial + Math.sin(tangent) * 0.95) * sp * dt
          break
        }
        case 'buffer': {
          // hang back behind the swarm (~distance 8), then pulse an empower aura
          const want = 8
          if (to.len > want + 1) {
            e.x += to.x * sp * dt
            e.z += to.z * sp * dt
          } else if (to.len < want - 1) {
            e.x -= to.x * sp * dt
            e.z -= to.z * sp * dt
          }
          const bdef = ENEMIES[e.defId]
          if (bdef?.buff) {
            e.aiTimer -= dt
            if (e.aiTimer <= 0) {
              e.aiTimer = bdef.buff.interval
              const r2 = bdef.buff.radius * bdef.buff.radius
              const dur = bdef.buff.interval * 1.25
              for (const o of world.enemies) {
                if (!o.alive || o === e || o.isBoss) continue
                if (dist2(e.x, e.z, o.x, o.z) <= r2) o.empoweredT = Math.max(o.empoweredT, dur)
              }
              e.empoweredT = Math.max(e.empoweredT, dur) // it rides its own banner too
              world.burst(e.x, e.z, e.color, 16, bdef.buff.radius * 0.4)
              world.floatText(e.x, e.z, 'EMPOWER', e.color)
            }
          }
          break
        }
        case 'healer': {
          // a support drone that hangs back (~distance 9) and periodically mends nearby wounded allies — burn it down
          // first or the swarm never thins. Bounded: heals a capped amount, never spawns, never heals bosses to full.
          const want = 9
          if (to.len > want + 1) {
            e.x += to.x * sp * dt
            e.z += to.z * sp * dt
          } else if (to.len < want - 1) {
            e.x -= to.x * sp * dt
            e.z -= to.z * sp * dt
          }
          const hdef = ENEMIES[e.defId]
          if (hdef?.heal) {
            e.aiTimer -= dt
            if (e.aiTimer <= 0) {
              e.aiTimer = hdef.heal.interval
              const r2 = hdef.heal.radius * hdef.heal.radius
              for (const o of world.enemies) {
                if (!o.alive || o === e || o.isBoss) continue
                if (o.hp >= o.maxHp) continue
                if (dist2(e.x, e.z, o.x, o.z) <= r2) o.hp = Math.min(o.maxHp, o.hp + hdef.heal.amount)
              }
              world.burst(e.x, e.z, e.color, 14, hdef.heal.radius * 0.4)
              world.floatText(e.x, e.z, 'MEND', e.color)
            }
          }
          break
        }
      }

      // terrain cliff-routing: enemies can't climb cliffs either — strip the uphill part of
      // their move so ridges become real chokepoints. Gated amp>0; (x,z) only — enemy height
      // stays render-only and never enters distance/targeting/collision math.
      if (world.terrainAmp > 0) {
        const dx = e.x - ox
        const dz = e.z - oz
        if (dx !== 0 || dz !== 0) {
          const g = terrainGradient(ox, oz, world.terrainSeed, world.terrainAmp, world.terrainCfg)
          const gmag = Math.hypot(g.gx, g.gz)
          if (gmag > CLIFF_SLOPE) {
            const hx = g.gx / gmag
            const hz = g.gz / gmag
            const along = dx * hx + dz * hz
            if (along > 0) {
              e.x = ox + (dx - along * hx)
              e.z = oz + (dz - along * hz)
            }
          }
        }
      }
    }

    if (world.obstacles.length > 0) pushOutOfObstacles(world, e)

    // contact damage to player — MP1: gated by THIS enemy's own cooldown, not a global i-frame,
    // so every enemy touching you lands its own hits and a dense pile actually hurts.
    // effective radius (radius*scale): a big enemy's body reaches the player at its visible edge.
    if (e.contactCd <= 0) {
      const cr = e.radius * e.scale + p.radius
      if (dist2(e.x, e.z, p.x, p.z) <= cr * cr) {
        damagePlayer(world, e.damage * (e.empoweredT > 0 ? 1.3 : 1), e)
        e.contactCd = CONTACT_CD
      }
    }
  }

  separateEnemies(world, dt)
}

// reused scratch for separate()'s per-enemy candidate sort — a typed-array numeric sort (no comparator callback)
// is far cheaper than `cand.sort((p,q)=>p-q)` at high local density (hundreds of candidates per enemy in a pile).
let _sepSort = new Int32Array(512)

/** Minimal shape the separation pass needs — lets it be unit-tested against a brute-force reference. */
export interface SepBody {
  x: number
  z: number
  radius: number
  scale: number
  alive: boolean
  isBoss: boolean
  stagger: number
}

/**
 * Enemy separation via a uniform spatial grid (MP2): O(n·k) instead of O(n²), so the swarm cap can
 * be lifted without the screen-filling crowd quadratic-blowing the frame. Byte-identical to the old
 * all-pairs loop: same outer i-ascending order, and for each `a` the candidate j's are taken from
 * the 3×3 cell neighborhood (cell ≥ 2× the largest body, so every overlapping pair is captured) and
 * processed in ascending j order — the exact sequence of pushes the flat loop applied, just without
 * the no-op far-pair checks. Pure + deterministic (the grid keys/orderings are stable).
 */
export function separate(list: SepBody[], _dt: number): void {
  const n = list.length
  if (n < 2) return
  let maxEffR = 0
  for (let i = 0; i < n; i++) {
    const e = list[i]
    if (e.isBoss) continue // bosses are skipped by the separation pass below — they must NOT size the grid cell, or a
    // giant boss (effR ~11.6) blows the cell up ~6× and the 3×3 query degrades back toward O(n²) at the full-swarm climax
    const r = e.radius * e.scale
    if (r > maxEffR) maxEffR = r
  }
  // ≥ max pair distance (2·maxEffR) + a margin for one frame's push displacement, so the 3×3
  // neighborhood is a strict superset of every pair that can overlap during this pass. The floor is just a
  // sanity minimum (the +1.0 margin already dominates for real bodies); a smaller floor = fewer no-op far
  // candidates per query when the swarm is all-small (dense early surges) — still byte-identical (superset holds).
  const cell = Math.max(1.4, maxEffR * 2 + 1.0)
  const grid = buildSpatialGrid(list, cell)
  const cand: number[] = []
  for (let i = 0; i < n; i++) {
    const a = list[i]
    if (!a.alive || a.isBoss || a.stagger > 0) continue // staggered enemies keep their knockback
    cand.length = 0
    gridNeighbors(grid, a.x, a.z, cand)
    // sort candidates ascending (identical push order to the old nested loop) via a typed-array numeric sort —
    // no per-comparison callback, so it's far cheaper than cand.sort((p,q)=>p-q) when the pile is dense.
    const k = cand.length
    if (k > _sepSort.length) _sepSort = new Int32Array(k * 2)
    for (let c = 0; c < k; c++) _sepSort[c] = cand[c]
    const sorted = _sepSort.subarray(0, k)
    sorted.sort()
    for (let c = 0; c < k; c++) {
      const j = sorted[c]
      if (j <= i) continue
      const b = list[j]
      if (!b.alive || b.isBoss || b.stagger > 0) continue
      const dx = b.x - a.x
      const dz = b.z - a.z
      const minD = a.radius * a.scale + b.radius * b.scale
      const d2 = dx * dx + dz * dz
      if (d2 > 0 && d2 < minD * minD) {
        const d = Math.sqrt(d2)
        const push = ((minD - d) / d) * 0.5
        const px = dx * push
        const pz = dz * push
        a.x -= px
        a.z -= pz
        b.x += px
        b.z += pz
      }
    }
  }
}

function separateEnemies(world: World, dt: number): void {
  separate(world.enemies, dt)
}

function spawnEnemyShot(world: World, e: Enemy, dx: number, dz: number): void {
  const speed = 9
  world.spawnProjectile(
    makeProjectile({
      ownerWeapon: 'enemy',
      srcEnemy: e.defId,
      behavior: 'straight',
      x: e.x,
      z: e.z,
      vx: dx * speed,
      vz: dz * speed,
      speed,
      damage: -e.damage, // negative marks an enemy projectile (handled below)
      crit: false,
      life: 3,
      pierce: 0,
      radius: 0.3,
      color: 0xff5a5a,
    }),
  )
}

/**
 * Execute one boss attack pattern (telegraphed AoE, line-charge, projectile fan,
 * or summon) and return the base cooldown before the next attack. Bosses cycle
 * their `patterns` list and hit harder/faster once enraged.
 */
/** Project a telegraph center back inside the playable disc so a wall-hugging boss can't drop danger zones (or its
 *  safe-lane) into the void where the player physically cannot stand (fake/unfair dodges). */
function clampArena(world: World, x: number, z: number): { x: number; z: number } {
  const r = world.map.radius - 2
  const d2 = x * x + z * z
  if (d2 > r * r) {
    const d = Math.sqrt(d2) || 1
    return { x: (x / d) * r, z: (z / d) * r }
  }
  return { x, z }
}

function executeBossPattern(world: World, e: Enemy, p: Player, pat: string): number {
  const to = dirTo(e.x, e.z, p.x, p.z)
  switch (pat) {
    case 'ring': {
      // a ring of telegraphed blasts around the boss, with safe lanes to dodge through
      const n = e.enraged ? 9 : 7
      const ringR = 5.5
      const gap = Math.floor(world.rng.next() * n)
      for (let i = 0; i < n; i++) {
        if (i === gap || i === (gap + 1) % n) continue
        const a = (i / n) * TAU
        const tp = clampArena(world, e.x + Math.cos(a) * ringR, e.z + Math.sin(a) * ringR)
        world.telegraphs.push({
          id: allocId(),
          x: tp.x,
          z: tp.z,
          radius: 2.4,
          timer: 1.1,
          maxTimer: 1.1,
          damage: e.damage * 1.3,
          owner: e.defId,
        })
      }
      return 4.0
    }
    case 'charge': {
      // telegraph a line, then lunge along it
      for (let i = 1; i <= 4; i++) {
        const tp = clampArena(world, e.x + to.x * i * 2.4, e.z + to.z * i * 2.4)
        world.telegraphs.push({
          id: allocId(),
          x: tp.x,
          z: tp.z,
          radius: 2.2,
          timer: 0.7,
          maxTimer: 0.7,
          damage: e.damage * 1.4,
          owner: e.defId,
        })
      }
      e.vx = to.x * e.speed * 6
      e.vz = to.z * e.speed * 6
      e.stagger = Math.max(e.stagger, 0.6)
      return 3.6
    }
    case 'beam': {
      // a fan of enemy shots, centered on a LED aim so a moving player can't just strafe out
      const n = e.enraged ? 11 : 7
      const spread = 1.2
      const lead = leadDir(world, e, 9)
      const base = Math.atan2(lead.z, lead.x) - spread / 2
      for (let i = 0; i < n; i++) {
        const a = base + (spread * i) / (n - 1)
        spawnEnemyShot(world, e, Math.cos(a), Math.sin(a))
      }
      return 3.0
    }
    case 'summon': {
      const sdef = ENEMIES[e.defId]
      const id = sdef?.summon?.id ?? 'swarmling'
      const count = (sdef?.summon?.count ?? 4) + (e.enraged ? 2 : 0)
      if (world.enemies.length < 300) {
        for (let i = 0; i < count; i++) {
          const a = world.rng.next() * TAU
          world.spawnEnemy(makeEnemyFromDef(world, id, e.x + Math.cos(a) * 1.5, e.z + Math.sin(a) * 1.5))
        }
      }
      return 4.5
    }
    case 'nova': {
      // PT3 phase-2 desperation: two concentric rings of telegraphed blasts, the outer ring staggered
      // later so the player must weave inner→outer. Each ring leaves a dodge gap.
      for (let ring = 0; ring < 2; ring++) {
        const ringR = 4.2 + ring * 3.0 // inner ring clears the boss body (radius up to ~2.4) so it reads as its own threat
        const n = 9 + ring * 3
        const gap = Math.floor(world.rng.next() * n)
        for (let i = 0; i < n; i++) {
          if (i === gap || i === (gap + 1) % n) continue
          const a = (i / n) * TAU
          const tp = clampArena(world, e.x + Math.cos(a) * ringR, e.z + Math.sin(a) * ringR)
          world.telegraphs.push({
            id: allocId(),
            x: tp.x,
            z: tp.z,
            radius: 2.2,
            timer: 0.9 + ring * 0.55, // outer ring detonates later → weave outward
            maxTimer: 0.9 + ring * 0.55,
            damage: e.damage * 1.35,
            owner: e.defId,
          })
        }
      }
      return 4.2
    }
    case 'slam':
    default: {
      // telegraphed AoE slam at the player (enraged adds a second offset blast)
      world.telegraphs.push({
        id: allocId(),
        x: p.x,
        z: p.z,
        radius: 4.5,
        timer: 0.9,
        maxTimer: 0.9,
        damage: e.damage * 1.6,
        owner: e.defId,
      })
      if (e.enraged) {
        const a = world.rng.next() * TAU
        const tp = clampArena(world, p.x + Math.cos(a) * 4, p.z + Math.sin(a) * 4)
        world.telegraphs.push({
          id: allocId(),
          x: tp.x,
          z: tp.z,
          radius: 3.5,
          timer: 1.0,
          maxTimer: 1.0,
          damage: e.damage * 1.4,
          owner: e.defId,
        })
      }
      return 3.0
    }
  }
}

/** Solid obstacles stop travelling projectiles (cover matters). Auras/orbits pass. */
export function collideProjectilesWithObstacles(world: World): void {
  if (world.obstacles.length === 0) return
  for (const pr of world.projectiles) {
    if (!pr.alive) continue
    if (pr.behavior !== 'straight' && pr.behavior !== 'homing') continue
    for (const o of world.obstacles) {
      if (!o.blocksShots) continue
      const rr = pr.radius + o.radius
      if (dist2(pr.x, pr.z, o.x, o.z) <= rr * rr) {
        world.burst(pr.x, pr.z, pr.color, 4, 0.8)
        pr.alive = false
        break
      }
    }
  }
}

/** Trigger a boss's signature power (gravity is handled inline as a constant pull). */
export function executeBossPower(world: World, e: Enemy, power: string): void {
  switch (power) {
    case 'weaponlock': {
      world.weaponsLocked = 4
      world.floatText(world.player.x, world.player.z, 'WEAPONS LOCKED!', 0xff3a2a, true)
      break
    }
    case 'shield': {
      e.bossShield = 3
      world.floatText(e.x, e.z, 'SHIELDED', 0x66e0ff, true)
      break
    }
    case 'clone': {
      if (world.enemies.length < 280) {
        for (let i = 0; i < 2; i++) {
          const a = world.rng.next() * TAU
          const m = makeEnemyFromDef(world, 'reaver', e.x + Math.cos(a) * 2.5, e.z + Math.sin(a) * 2.5)
          m.isElite = true
          m.hp *= 2
          m.maxHp *= 2
          m.color = 0xffd23d
          world.spawnEnemy(m)
        }
        world.floatText(e.x, e.z, 'REINFORCEMENTS!', 0xffd23d, true)
      }
      break
    }
    case 'quake': {
      // golem: erupt a ring of telegraphed ground hazards around the player — area denial. rng-free (fixed angles).
      const pl = world.player
      const n = 5
      for (let i = 0; i < n; i++) {
        const a = (i / n) * TAU
        const tp = clampArena(world, pl.x + Math.cos(a) * 5, pl.z + Math.sin(a) * 5)
        world.telegraphs.push({ id: allocId(), x: tp.x, z: tp.z, radius: 2.6, timer: 1.1, maxTimer: 1.1, damage: e.damage * 1.2, owner: e.defId })
      }
      world.floatText(e.x, e.z, 'QUAKE!', 0xc8923a, true)
      break
    }
    case 'empower': {
      // sandwyrm: rally — frenzies the surrounding swarm (faster + harder-hitting via empoweredT). rng-free.
      for (const o of world.enemies) {
        if (!o.alive || o === e || o.isBoss) continue
        if (dist2(e.x, e.z, o.x, o.z) <= 196) o.empoweredT = Math.max(o.empoweredT, 6)
      }
      world.floatText(e.x, e.z, 'RALLY!', 0xff7a2d, true)
      break
    }
    case 'drain': {
      // reaper: life-siphon — bites the player and heals itself. rng-free.
      damagePlayer(world, e.damage * 0.7, e, e.defId)
      e.hp = Math.min(e.maxHp, e.hp + e.maxHp * 0.04)
      world.floatText(e.x, e.z, 'SIPHON', 0x5be0c8, true)
      break
    }
  }
}

/** Resolve queued AoE blasts (bomber deaths) against enemies and the player. */
export function processExplosions(world: World): void {
  if (world.pendingExplosions.length === 0) return
  world.inExplosions = true // suppress procs so AoE kills can't cascade-trigger more AoE
  // snapshot the queue: resolve only blasts present at frame start; bombers chain-detonated by THIS pass
  // re-queue at indices >= n and resolve next tick — so a dense bomber cluster can't cascade in one frame.
  const n = world.pendingExplosions.length
  for (let i = 0; i < n; i++) {
    const ex = world.pendingExplosions[i]
    const r2 = ex.radius * ex.radius
    world.blast(ex.x, ex.z, ex.radius, ex.color) // CF2: show the actual lethal footprint (render-only)
    for (const e of world.enemies) {
      if (!e.alive) continue
      if (dist2(ex.x, ex.z, e.x, e.z) <= r2) {
        const to = dirTo(ex.x, ex.z, e.x, e.z)
        dealDamage(world, e, ex.damage, { knockX: to.x, knockZ: to.z, knockForce: 4, lifesteal: false, bypassArmor: true, source: ex.weapon })
      }
    }
    if (dist2(ex.x, ex.z, world.player.x, world.player.z) <= r2) {
      damagePlayer(world, ex.damage)
    }
  }
  world.pendingExplosions.splice(0, n) // keep any chain blasts queued this pass for the next tick
  world.inExplosions = false
}

export function damagePlayer(world: World, raw: number, source?: Enemy, srcId?: string): void {
  const p = world.player
  const s = world.stats
  if (!p.alive || p.invuln > 0) return
  // dodge
  if (s.dodge > 0 && world.rng.next() < s.dodge) {
    if (source) world.floatText(p.x, p.z, 'dodge', 0x9fe8ff)
    return
  }
  let dmg = applyArmor(raw, s.armor)
  // Juggernaut: heavy plating soaks 30% of all incoming damage.
  if (world.passive === 'juggernaut') dmg *= 0.7
  // Glass cannon: the price of the damage surge — takes 60% more.
  else if (world.passive === 'glasscannon') dmg *= 1.6
  // shield absorbs first
  if (p.shield > 0) {
    const absorbed = Math.min(p.shield, dmg)
    p.shield -= absorbed
    dmg -= absorbed
  }
  if (dmg <= 0) return
  p.hp -= dmg
  // remember the most recent damager so the recap can name the killing blow
  const by = source?.defId ?? srcId
  if (by) world.killedBy = by
  p.flash = 0.15
  // MP1: NO blanket post-hit i-frame — density must bite. Dodge i-frames come only from a
  // deliberate dash (systems.ts dash) / airborne jump; contact is throttled per-enemy via contactCd;
  // projectiles, AoE and telegraphs are each one-shot per event so they can't multi-hit per frame.
  // thorns
  if (s.thorns > 0 && source && source.alive) {
    dealDamage(world, source, dmg * s.thorns, { silent: true, lifesteal: false }) // reflect damage actually taken (post armor/shield), not raw
  }
  if (p.hp <= 0) p.hp = 0
}

// ---------------------------------------------------------------------------
// Enemy projectiles hitting the player
// ---------------------------------------------------------------------------

export function collideEnemyProjectiles(world: World): void {
  const p = world.player
  for (const pr of world.projectiles) {
    if (!pr.alive || pr.damage >= 0) continue // enemy shots carry negative damage
    const rr = pr.radius + p.radius
    if (dist2(pr.x, pr.z, p.x, p.z) <= rr * rr) {
      damagePlayer(world, -pr.damage, undefined, pr.srcEnemy)
      pr.alive = false
    }
  }
}

// ---------------------------------------------------------------------------
// Status helpers
// ---------------------------------------------------------------------------

// DoT is ADDITIVE: re-applying stacks more DPS (capped) instead of just refreshing,
// so burn/poison builds actually scale. The applied dps already carries the hit's
// +damage and crit, so DoT inherits those too.
export function applyBurn(e: Enemy, time: number, dps: number): void {
  e.burn = Math.max(e.burn, time)
  e.burnDps = Math.min(e.burnDps + dps, Math.max(dps * 10, e.maxHp * 0.4))
}
export function applyPoison(e: Enemy, time: number, dps: number): void {
  e.poison = Math.max(e.poison, time)
  e.poisonDps = Math.min(e.poisonDps + dps, Math.max(dps * 10, e.maxHp * 0.4))
  e.bleedStacks = Math.min(20, e.bleedStacks + 1) // visible stacking bleed
}

// ---------------------------------------------------------------------------
// Pickups
// ---------------------------------------------------------------------------

/**
 * Consolidate dense fields of XP gems: nearby same-tier, un-magnetized gems merge into
 * one (value summed, tier upgraded when it crosses a breakpoint) so the ground stays
 * readable at high enemy counts. Total XP is preserved — this is purely visual cleanup.
 * O(n) via a grid bucket; only runs once the field is actually crowded.
 */
function mergeXpGems(world: World): void {
  let xpCount = 0
  for (const pk of world.pickups) if (pk.alive && pk.kind === 'xp' && !pk.magnetized) xpCount++
  if (xpCount < 30) return
  const CELL = 1.2
  const cells = new Map<string, Pickup>()
  for (const pk of world.pickups) {
    if (!pk.alive || pk.kind !== 'xp' || pk.magnetized) continue
    const tier = pk.tier ?? 1
    const key = tier + '|' + Math.round(pk.x / CELL) + '|' + Math.round(pk.z / CELL)
    const host = cells.get(key)
    if (host) {
      host.value += pk.value
      const nt = xpGemTier(host.value)
      if (nt > (host.tier ?? 1)) {
        host.tier = nt
        host.radius = gemRadius(nt) // grab radius must track the upgraded sprite size (CF6)
        world.burst(host.x, host.z, 0x9fd8ff, 4, 0.5) // upgrade sparkle
      }
      pk.alive = false
    } else {
      cells.set(key, pk)
    }
  }
}

export function updatePickups(world: World, dt: number): { leveled: boolean } {
  const p = world.player
  const s = world.stats
  const range = s.pickupRange
  const range2 = range * range
  let leveled = false

  mergeXpGems(world)

  for (const pk of world.pickups) {
    if (!pk.alive) continue
    // gold is handed DIRECTLY to the player (no ground clutter) — collected the instant it drops, wherever it landed.
    // (collectPickup credits goldGain + bumps pickupsThisFrame so the coin chime + HUD tick still fire.)
    if (pk.kind === 'gold') {
      collectPickup(world, pk)
      pk.alive = false
      continue
    }
    // settle bob/gravity
    if (pk.y > 0.3) {
      pk.vy -= 14 * dt
      pk.y += pk.vy * dt
      if (pk.y < 0.3) {
        pk.y = 0.3
        pk.vy = 0
      }
    }
    pk.bob += dt * 3

    const d2 = dist2(pk.x, pk.z, p.x, p.z)
    // magnetize within range (chests & vendors must be walked into)
    if (pk.kind !== 'chest' && pk.kind !== 'vendor' && (pk.magnetized || d2 < range2)) {
      pk.magnetized = true
      const to = dirTo(pk.x, pk.z, p.x, p.z)
      const pullSpeed = Math.max(12, 12 + (range - Math.sqrt(d2)) * 1.5) // clamp: a magnet must always PULL, never repel distant gems
      pk.x += to.x * pullSpeed * dt
      pk.z += to.z * pullSpeed * dt
      // vacuum stream: trailing sparkles toward the player
      if (world.particles.length < 2600 && world.rng.next() < 0.5) {
        world.particles.push({
          id: 0,
          x: pk.x,
          z: pk.z,
          y: pk.y + 0.3,
          vx: 0,
          vz: 0,
          vy: 0.6,
          life: 0.22,
          maxLife: 0.22,
          color: 0x35c8ff, // gold is auto-collected before this point, so the vacuum stream is only XP/heal/etc.
          size: 0.13,
        })
      }
    }

    // collect
    const cr = p.radius + pk.radius + 0.2
    if (d2 < cr * cr) {
      if (collectPickup(world, pk)) leveled = true
      pk.alive = false
    }
  }
  return { leveled }
}

function collectPickup(world: World, pk: Pickup): boolean {
  const p = world.player
  const s = world.stats
  switch (pk.kind) {
    case 'xp': {
      const gain = pk.value * s.xpGain
      p.xp += gain
      world.xpCollected += gain
      // big gems pop a readable XP value (tier 1 trash stays quiet to avoid spam)
      if ((pk.tier ?? 1) >= 3) world.floatText(p.x, p.z, `+${Math.round(gain)} XP`, 0xb060ff)
      return checkLevelUp(world)
    }
    case 'gold': {
      world.gainGold(Math.round(pk.value * s.goldGain))
      world.pickupsThisFrame++
      return false
    }
    case 'heal': {
      healPlayer(world, pk.value)
      world.floatText(p.x, p.z, `+${pk.value}`, 0x66ff88)
      world.pickupsThisFrame++
      return false
    }
    case 'magnet': {
      for (const o of world.pickups) if (o.kind === 'xp') o.magnetized = true
      world.emit('magnet')
      return false
    }
    case 'bomb': {
      // screen-clear nuke: actually kill every non-boss so it pays out loot + xp
      for (const e of world.enemies) if (e.alive && !e.isBoss) killEnemy(world, e)
      world.emit('nuke')
      return false
    }
    case 'chest': {
      world.emit('chest')
      return false
    }
    case 'vendor': {
      world.emit('shop')
      return false
    }
  }
  return false
}

function checkLevelUp(world: World): boolean {
  let leveled = false
  const p = world.player
  while (p.xp >= p.xpToNext) {
    p.xp -= p.xpToNext
    p.level++
    p.xpToNext = xpCurve(p.level)
    world.pendingLevelUps++
    leveled = true
    // 'dice' hero (Roll): each level-up rolls ONE random permanent stat surge — a different build every run. Deterministic
    // (world.rng); accumulated into world.diceStats and applied on the next recomputeStats (which a level-up pick triggers).
    if (world.gimmick === 'dice') {
      const ds = world.diceStats
      const roll = Math.floor(world.rng.next() * 6)
      if (roll === 0) { ds.damage += 0.07; world.floatText(p.x, p.z, 'ROLL: PWR', 0x6ad06a, true) }
      else if (roll === 1) { ds.attackSpeed += 0.07; world.floatText(p.x, p.z, 'ROLL: RPM', 0x6ad0ff, true) }
      else if (roll === 2) { ds.critChance += 0.03; world.floatText(p.x, p.z, 'ROLL: CRIT', 0xffd23b, true) }
      else if (roll === 3) { ds.critMult += 0.12; world.floatText(p.x, p.z, 'ROLL: x2', 0xff7a3d, true) }
      else if (roll === 4) { ds.maxHp += 14; world.floatText(p.x, p.z, 'ROLL: HP', 0xff5a6a, true) }
      else { ds.armor += 2; world.floatText(p.x, p.z, 'ROLL: DEF', 0xc6ccd8, true) }
    }
  }
  return leveled
}

function xpCurve(level: number): number {
  return xpForLevel(level)
}

// ---------------------------------------------------------------------------
// FX update
// ---------------------------------------------------------------------------

export function updateFx(world: World, dt: number): void {
  for (const f of world.floaters) {
    f.life -= dt
    f.y += f.vy * dt
    f.vy *= Math.pow(0.1, dt)
  }
  world.floaters = world.floaters.filter((f) => f.life > 0)

  for (const pt of world.particles) {
    pt.life -= dt
    pt.x += pt.vx * dt
    pt.z += pt.vz * dt
    pt.y += pt.vy * dt
    pt.vy -= 9 * dt
    if (pt.y < 0) {
      pt.y = 0
      pt.vy *= -0.3
    }
  }
  world.particles = world.particles.filter((pt) => pt.life > 0)

  // CF2: age + cull explosion footprint rings (render-only)
  if (world.blasts.length > 0) {
    for (const b of world.blasts) b.t += dt
    world.blasts = world.blasts.filter((b) => b.t < b.maxT)
  }

  if (world.corpses.length > 0) {
    for (const c of world.corpses) {
      c.life -= dt
      c.y += c.vy * dt
      c.vy -= 16 * dt
      // horizontal bonk slide along the hit direction, decaying to a stop (fixed-dt sim ⇒ deterministic)
      c.x += c.vx * dt
      c.z += c.vz * dt
      c.vx *= 0.86
      c.vz *= 0.86
      if (c.y < 0) {
        c.y = 0
        c.vy = 0
      }
    }
    world.corpses = world.corpses.filter((c) => c.life > 0)
  }

  for (const b of world.breakables) if (b.flash > 0) b.flash = Math.max(0, b.flash - dt)
}

export function cleanupDead(world: World): void {
  if (world.enemies.length > 0) world.enemies = world.enemies.filter((e) => e.alive)
  if (world.projectiles.length > 0) world.projectiles = world.projectiles.filter((p) => p.alive)
  if (world.pickups.length > 0) world.pickups = world.pickups.filter((p) => p.alive)
}

// ---------------------------------------------------------------------------
// Spawn director
// ---------------------------------------------------------------------------

export function updateSpawns(world: World, dt: number): void {
  const t = world.time
  // endless mode keeps cranking the difficulty after the stage is cleared
  if (world.endless) world.difficulty += 0.03 * dt

  // escalation rhythm: peaks and valleys instead of a flat ramp. A brief calm
  // telegraphs an incoming surge, then the spawn rate spikes, then it settles.
  const surgeMul = updateSurge(world, dt, t)

  // directional tide: drift the spawn-front angle so pressure shifts around the player
  updateTide(world, dt)

  // Final Swarm: a RISING overtime climax (Megabonk), not a flat spike — the multiplier ramps from 1.8x up to 3.2x over
  // ~70s as finalSwarmT grows. (Canary-neutral: finalSwarm is only set when the boss is summoned, well past the 240s/
  // PT windows, so finalSwarmT stays 0 and this evaluates to 1x in the canary.)
  if (world.finalSwarm) world.finalSwarmT += dt
  const finalMul = world.finalSwarm ? Math.min(3.2, 1.8 + world.finalSwarmT * 0.02) : 1
  // spawn rate ramps with time, map intensity and curse — modulated by the surge rhythm + the final-swarm climax
  const intensity = world.difficulty
  const ratePerSec = (1.5 + t * 0.05) * intensity * (1 + world.curse * 0.08) * finalMul * surgeMul
  world.spawnAccum += ratePerSec * dt

  // hard cap to protect performance. Separation cost grows super-linearly with local density (the swarm piling
  // on the player), so 700 caused real frame drops (~22fps) at the 3-min mark; 500 is still a screen-filling
  // horde but keeps the per-tick sim within frame budget so the framerate holds. (Canary bound is <=760.)
  const cap = 500
  while (world.spawnAccum >= 1 && world.enemies.length < cap) {
    world.spawnAccum -= 1
    spawnOne(world)
  }
  if (world.spawnAccum > 5) world.spawnAccum = 5

  // elites every ~35s — but an ELITE-HUNT stage (MP6) pours them in fast and from the start so the
  // goal (kill N elites to open the portal) is actually achievable and reshapes the stage's pacing
  const hunting = world.stageGoal === 'elite-hunt'
  world.eliteTimer -= dt
  if (world.eliteTimer <= 0 && (t > 40 || hunting)) {
    world.eliteTimer = hunting ? 6 : 35
    spawnElite(world)
  }

  // mini-boss beats between the main bosses: a named champion with an HP bar + reward.
  // Cadence tightens with stage; skipped while a real boss is on the field.
  world.miniBossTimer -= dt
  if (world.miniBossTimer <= 0 && t > 30 && (world.objective === 'survive' || world.objective === 'endless')) {
    world.miniBossTimer = Math.max(34, 55 - world.stage * 5)
    if (!world.enemies.some((e) => e.alive && e.isBoss)) spawnMiniBoss(world)
  }

  // bosses on a schedule — paced faster on later stages/tiers and under curse
  if (t >= world.nextBossAt) {
    spawnBoss(world)
    const interval = (150 * world.bossRateMul) / (1 + world.curse * 0.25) / (1 + world.stage * 0.18)
    world.nextBossAt += interval
  }

  // roaming vendor appears within sight; player walks into it to open the shop
  world.vendorTimer -= dt
  if (world.vendorTimer <= 0 && t > 25) {
    world.vendorTimer = 55
    const a = world.rng.next() * TAU
    const ring = 9 + world.rng.range(0, 3)
    let x = world.player.x + Math.cos(a) * ring
    let z = world.player.z + Math.sin(a) * ring
    const r = world.map.radius - 3
    const d = Math.hypot(x, z)
    if (d > r) {
      x = (x / d) * r
      z = (z / d) * r
    }
    const v = world.spawnPickup('vendor', x, z, 0)
    v.radius = 1.1
    world.emit('vendor')
  }

  // shrines appear within sight; player walks in to trigger them
  world.shrineTimer -= dt
  if (world.shrineTimer <= 0 && t > 18) {
    world.shrineTimer = 40
    // moai + microwave are fixed landmarks placed at run start, not random timer shrines
    const types: ShrineType[] = ['magnet', 'gold', 'heal', 'treasure', 'battle', 'curse', 'charge']
    const type = types[world.rng.int(0, types.length - 1)]
    const a = world.rng.next() * TAU
    const ring = 12 + world.rng.range(0, 4)
    let x = world.player.x + Math.cos(a) * ring
    let z = world.player.z + Math.sin(a) * ring
    const r = world.map.radius - 3
    const d = Math.hypot(x, z)
    if (d > r) {
      x = (x / d) * r
      z = (z / d) * r
    }
    const s: Shrine = { id: allocId(), x, z, type, used: false }
    world.shrines.push(s)
    world.emit('shrine-spawn')
  }
}

export function updateTelegraphs(world: World, dt: number): void {
  if (world.telegraphs.length === 0) return
  const p = world.player
  for (const tg of world.telegraphs) {
    tg.timer -= dt
    if (tg.timer <= 0) {
      if (dist2(tg.x, tg.z, p.x, p.z) <= tg.radius * tg.radius) damagePlayer(world, tg.damage, undefined, tg.owner)
      world.burst(tg.x, tg.z, 0xff5a3d, 28, 2)
    }
  }
  world.telegraphs = world.telegraphs.filter((tg) => tg.timer > 0)
}

/** Spawn the climactic stage boss (toughest in the map's pool, beefed up). */
export function spawnFinalBoss(world: World): void {
  const pool =
    world.map.bossPool && world.map.bossPool.length > 0
      ? world.map.bossPool.map((id) => ENEMIES[id]).filter(Boolean)
      : BOSS_LIST
  // Escalate the boss per stage in a tier gauntlet — the toughest (pool is ascending-by-HP) is reserved for the LAST
  // stage; a single-stage (tier-1) run still gets the toughest (frac=1, unchanged). Indexed by stage, not rng.
  const frac = world.stages > 1 ? world.stage / (world.stages - 1) : 1
  let def = pool[Math.round(frac * (pool.length - 1))]
  // Boss Curse ladder: the hidden bosses answer the portal at high curse — the apex Devourer (≥8) outranks the Voidlord (≥5)
  if (world.curse >= 8 && ENEMIES['boss_devourer']) def = ENEMIES['boss_devourer']
  else if (world.curse >= 5 && ENEMIES['boss_voidlord']) def = ENEMIES['boss_voidlord']
  // the stage boss erupts from the portal if one was activated, else nearby
  const pos = world.portal ? { x: world.portal.x, z: world.portal.z } : spawnPos(world)
  const e = makeEnemyFromDef(world, def.id, pos.x, pos.z)
  e.hp *= 1.6
  e.maxHp *= 1.6
  e.scale *= 1.15
  world.spawnEnemy(e)
  world.finalBossId = e.id
  world.emit('boss')
}

/** Spawn the boss teleporter to find & activate (explore-to-summon, Risk-of-Rain style). NOTE: does NOT flip the
 *  objective — the caller decides (it can appear early as an explore target while the objective is still 'survive'). */
export function spawnPortal(world: World): void {
  const a = world.rng.next() * TAU
  const r = (world.map.radius - 6) * 0.82
  world.portal = { x: Math.cos(a) * r, z: Math.sin(a) * r, channel: 0, active: false }
  world.emit('portal')
}

/** Channel the portal while the player stands in it; activating it summons the boss. */
export function updatePortal(world: World, dt: number): void {
  const pt = world.portal
  if (!pt || pt.active) return
  const p = world.player
  const cr = p.radius + 2.4
  if (dist2(pt.x, pt.z, p.x, p.z) <= cr * cr) {
    pt.channel = Math.min(1, pt.channel + dt / 2.5) // ~2.5s to activate
    if (pt.channel >= 1) {
      pt.active = true
      world.finalSwarm = true // overtime climax
      spawnFinalBoss(world)
      world.objective = 'finalboss'
      world.emit('finalboss')
    }
  }
}

export function updateShrines(world: World, dt: number): void {
  const p = world.player
  if (world.dmgSurgeT > 0) world.dmgSurgeT = Math.max(0, world.dmgSurgeT - dt) // tick down the Charge-shrine damage surge
  for (const s of world.shrines) {
    if (s.used) continue
    const cr = p.radius + 1.3
    const inRange = dist2(s.x, s.z, p.x, p.z) <= cr * cr
    if (s.type === 'charge') {
      // Megabonk-style: stand inside to fill the meter (~3s, mirrors updatePortal channel); the buff fires only when full.
      if (inRange) {
        s.charge = Math.min(1, (s.charge ?? 0) + dt / 3)
        if (s.charge >= 1) {
          s.used = true
          triggerShrine(world, s)
        }
      } else if ((s.charge ?? 0) > 0) {
        s.charge = Math.max(0, (s.charge ?? 0) - dt * 0.5) // drains slowly if you step out before it's full
      }
      continue
    }
    if (inRange) {
      s.used = true
      triggerShrine(world, s)
    }
  }
}

function triggerShrine(world: World, s: Shrine): void {
  const p = world.player
  world.burst(s.x, s.z, 0xffd23d, 30, 1.6)
  switch (s.type) {
    case 'magnet':
      for (const pk of world.pickups) if (pk.kind === 'xp' || pk.kind === 'gold') pk.magnetized = true
      world.emit('shrine:magnet')
      break
    case 'gold': {
      const g = Math.round(40 + world.time * 0.7)
      world.gainGold(Math.round(g * world.stats.goldGain))
      world.floatText(p.x, p.z, `+${g}`, 0xffd23d, true)
      world.emit('shrine:gold')
      break
    }
    case 'heal':
      healPlayer(world, world.stats.maxHp) // honors the Iron Man noHeal challenge
      p.shield = Math.max(p.shield, 50)
      world.floatText(p.x, p.z, '+', 0x66ff88, true)
      world.emit('shrine:heal')
      break
    case 'treasure':
      world.spawnPickup('chest', p.x + 1.5, p.z, 1)
      world.spawnPickup('chest', p.x - 1.5, p.z, 1)
      world.emit('shrine:treasure')
      break
    case 'battle': {
      // risk/reward: a sudden elite wave, with loot waiting at the shrine
      const pool = eligibleEnemies(world.time, world.map.enemyPool)
      for (let i = 0; i < 12 && pool.length > 0; i++) {
        const def = pool[world.rng.int(0, pool.length - 1)]
        const a = (i / 12) * TAU
        const e = makeEnemyFromDef(world, def.id, p.x + Math.cos(a) * 7, p.z + Math.sin(a) * 7)
        e.isElite = true
        e.hp *= 3
        e.maxHp *= 3
        e.damage *= 1.5 // these gold 3x-HP elites must also bite like elites (matches spawnElite) — the "risk" half of the shrine
        e.xp *= 3
        e.gold *= 3
        e.scale *= 1.3
        e.color = 0xffd23d
        world.spawnEnemy(e)
      }
      world.spawnPickup('gold', s.x, s.z, Math.round(60 + world.time))
      world.spawnPickup('chest', s.x, s.z, 1)
      world.emit('shrine:battle')
      break
    }
    case 'curse':
      world.curse++
      world.spawnPickup('chest', s.x, s.z, 1)
      world.floatText(p.x, p.z, `CURSE ${world.curse}`, 0xff2e97, true)
      world.emit('shrine:curse')
      break
    case 'charge':
      // channeled to full → a timed +50% damage SURGE (read in rollDamage via world.dmgSurgeT). Deterministic (no rng).
      world.dmgSurgeT = 10
      world.floatText(p.x, p.z, 'SURGE!', 0x66e0ff, true)
      world.emit('shrine:charge')
      break
    case 'moai':
      // blessing resolved by the game layer (which owns recompute/UI)
      world.emit('moai')
      break
    case 'microwave':
      world.emit('microwave')
      break
  }
}

function spawnPos(world: World): { x: number; z: number } {
  // spawn just outside the player's view ring, biased toward the drifting "tide" front.
  // The front tightens during a surge so the wave reads as coming from one shifting side.
  const spread = world.surgePhase === 'surge' ? 0.6 : world.surgePhase === 'calm' ? 0.95 : 1.6
  const a = world.tideAngle + (world.rng.next() * 2 - 1) * spread
  const ring = 24 + world.rng.range(0, 6)
  let x = world.player.x + Math.cos(a) * ring
  let z = world.player.z + Math.sin(a) * ring
  // keep inside arena
  const r = world.map.radius - 2
  const d = Math.hypot(x, z)
  if (d > r) {
    x = (x / d) * r
    z = (z / d) * r
  }
  return { x, z }
}

function spawnOne(world: World): void {
  const pool = eligibleEnemies(world.time, world.map.enemyPool)
  if (pool.length === 0) return
  const weights = pool.map((e) => e.weight ?? 1)
  const idx = world.rng.weightedIndex(weights)
  const def = pool[idx]
  const pos = spawnPos(world)
  world.spawnEnemy(makeEnemyFromDef(world, def.id, pos.x, pos.z))
}

/** Elite/mini-boss behavioral AFFIX — distinct THREATS that reshape the fight, not just bigger HP bars. Picked
 *  from a sim-only sequence counter (NO rng draw, so it doesn't perturb the spawn/drop stream; NOT the entity id, so it's
 *  reproducible per seed regardless of render framerate). Non-lethal affixes only
 *  for now (they raise time-to-kill, not incoming DPS) so difficulty reshapes without spiking. */
export const AFFIXES = ['warded', 'shielded', 'swift', 'mending'] as const
export function applyAffix(world: World, e: Enemy): void {
  e.affix = AFFIXES[world.affixSeq++ % AFFIXES.length]
  if (e.affix === 'warded') e.frontArmor = Math.max(e.frontArmor, 0.6) // frontal weapon hits heavily reduced — flank / crit / AoE it
  else if (e.affix === 'shielded') e.shieldCd = 3 // periodic ~0.8s damage-immunity pulse (re-armed each cycle in updateEnemies)
  // 'swift' (+35% move speed, handled in the movement calc) and 'mending' (slow self-heal, handled per-tick) need no init
}

function spawnElite(world: World): void {
  const pool = eligibleEnemies(world.time, world.map.enemyPool)
  if (pool.length === 0) return
  const def = world.rng.pick(pool)
  const pos = spawnPos(world)
  const e = makeEnemyFromDef(world, def.id, pos.x, pos.z)
  e.isElite = true
  e.hp *= 6
  e.maxHp *= 6
  e.damage *= 1.5
  e.xp *= 5
  e.gold *= 4
  e.scale *= 1.5
  e.color = 0xffd23d
  applyAffix(world, e)
  world.spawnEnemy(e)
  world.emit('elite')
}

/** Drift the directional-tide angle, occasionally re-rolling its speed/direction. */
function updateTide(world: World, dt: number): void {
  world.tideAngle += world.tideDrift * dt
  world.tideTimer -= dt
  if (world.tideTimer <= 0) {
    world.tideTimer = 6 + world.rng.next() * 6
    world.tideDrift = (world.rng.next() < 0.5 ? -1 : 1) * (0.12 + world.rng.next() * 0.25)
  }
}

/**
 * Aim direction from an enemy that LEADS the player's velocity: predict where the
 * player will be when the shot arrives. Lead strength scales with difficulty/time
 * (gentle on Easy/early, near-full later/Cooked) so it stays fair.
 */
export function leadDir(world: World, e: Enemy, speed: number): { x: number; z: number } {
  const p = world.player
  const dist = Math.hypot(p.x - e.x, p.z - e.z) || 1
  const travel = dist / speed
  const lead = Math.max(0.2, Math.min(1, (world.difficulty - 0.6) * 0.5 + world.time / 500))
  const tx = p.x + p.vx * travel * lead
  const tz = p.z + p.vz * travel * lead
  const ax = tx - e.x
  const az = tz - e.z
  const al = Math.hypot(ax, az) || 1
  return { x: ax / al, z: az / al }
}

/**
 * Drive the surge rhythm and return the spawn-rate multiplier for this frame.
 * idle → (telegraphed) calm lull → surge spike → idle. Only runs during 'survive'
 * so it never fights the Final Swarm. Surges come more often on later stages.
 */
function updateSurge(world: World, dt: number, t: number): number {
  if (world.objective !== 'survive' && world.objective !== 'endless') {
    world.surgePhase = 'idle'
    return 1
  }
  world.surgeT -= dt
  if (world.surgePhase === 'calm') {
    if (world.surgeT <= 0) {
      world.surgePhase = 'surge'
      world.surgeT = 9
    }
    return 0.4 // lull before the storm
  }
  if (world.surgePhase === 'surge') {
    if (world.surgeT <= 0) {
      world.surgePhase = 'idle'
      world.nextSurgeAt = t + Math.max(20, 38 - world.stage * 4)
    }
    return 2.4 // the wave hits
  }
  // idle
  if (t > 28 && t >= world.nextSurgeAt) {
    world.surgePhase = 'calm'
    world.surgeT = 3.2
    world.emit('surge') // telegraph the incoming wave
  }
  return 1
}

/**
 * A mini-boss "beat": a named champion tougher than an elite but short of a full
 * boss — gets an HP bar, a name-card and a chest on death. Scales with stage/curse.
 */
function spawnMiniBoss(world: World): void {
  const pool = eligibleEnemies(world.time, world.map.enemyPool)
  if (pool.length === 0) return
  // prefer a beefier base enemy (highest hp in pool) so the champion reads as a threat
  const def = pool.reduce((a, b) => (b.hp > a.hp ? b : a), pool[0])
  const pos = spawnPos(world)
  const e = makeEnemyFromDef(world, def.id, pos.x, pos.z)
  const stageMul = 1 + world.stage * 0.35
  e.isElite = true
  e.isMiniBoss = true
  e.hp *= 13 * stageMul
  e.maxHp = e.hp
  e.damage *= 1.8
  e.xp *= 9
  e.gold *= 7
  e.scale *= 1.9
  e.speed *= 0.9
  e.color = 0xff7a2d
  applyAffix(world, e)
  world.spawnEnemy(e)
  world.emit('miniboss')
}

function spawnBoss(world: World): void {
  const bossPool =
    world.map.bossPool && world.map.bossPool.length > 0
      ? world.map.bossPool.map((id) => ENEMIES[id]).filter(Boolean)
      : BOSS_LIST
  const idx = Math.min(bossPool.length - 1, world.bossesKilled)
  const def = bossPool[idx]
  const pos = spawnPos(world)
  const e = makeEnemyFromDef(world, def.id, pos.x, pos.z)
  world.spawnEnemy(e)
  world.emit('boss')
}

export { dist }
